/**
 * Pricing Calculation Engine
 * 
 * Ported directly from original VBA application (CastorWheelsCoding.bas).
 * Replicates 100% of the pricing rules, rounding logic (WorksheetFunction.RoundUp),
 * assembly component price summation (13 components), and tax/terms calculations.
 */

import type { PriceListItem } from '../types';

export interface ParsedItemCode {
  castorType: string;
  wheelType: string;
  wheelMaterial: string;
  size: string;
  bearing: string;
  bearingQty: number;
  circlip: string;
  spacer: string;
  bush: string;
  threadGuard: string;
  nutBolt: string;
  ci: string;
  flange: string;
  adapter: string;
  axle: string;
  footBrake: string;
}

export interface LineItemInput {
  sno: number;
  item_code: string;
  description?: string;
  quantity: number;
  rate: number;
  discount_percent?: number;
}

export interface LineItemCalculation {
  sno: number;
  item_code: string;
  description?: string;
  quantity: number;
  rate: number;
  amount: number;
  discount_percent: number;
  discount_amount: number;
  final_amount: number;
}

export interface FinancialCalculationResult {
  subtotal: number;
  discountPercent: number;
  discountAmount: number;
  pfPercent: number;
  pfAmount: number;
  pf?: number;
  others: number;
  taxableBase: number;
  gstPercent: number;
  gstAmount: number;
  gst?: number;
  total: number;
  amountInWords: string;
  remarks?: string;
}

/**
 * Replicate VBA WorksheetFunction.RoundUp(number, 0)
 */
export function roundUp(val: number): number {
  if (isNaN(val) || val === 0) return 0;
  return Math.ceil(val);
}

/**
 * Parse item code into components
 * Examples:
 *   SF1/SW/PP/F/615/BB6203/CIR/GB/PTG/NB
 *   SPF3/SW/62/PP/R/S/62/BB6203/CIR/GB/ETG/NB
 */
export function parseItemCode(itemCode: string): ParsedItemCode {
  if (!itemCode || typeof itemCode !== 'string') {
    return {
      castorType: '',
      wheelType: '',
      wheelMaterial: '',
      size: '',
      bearing: '',
      bearingQty: 1,
      circlip: '',
      spacer: '',
      bush: '',
      threadGuard: '',
      nutBolt: '',
      ci: '',
      flange: '',
      adapter: '',
      axle: '',
      footBrake: '',
    };
  }

  const parts = itemCode.split('/').map((p) => p.trim());

  // Determine bearing count from description or parts if 2 bearings specified
  let bearingQty = 1;
  if (itemCode.includes('2 Pcs') || itemCode.includes('2BB') || itemCode.includes('2 BB')) {
    bearingQty = 2;
  }

  return {
    castorType: parts[0] || '',
    wheelType: parts[1] || '',
    wheelMaterial: parts[2] || '',
    size: parts[3] || '',
    bearing: parts[4] || '',
    bearingQty,
    circlip: parts[5] || '',
    spacer: '',
    bush: parts[6] || '',
    threadGuard: parts[7] || '',
    nutBolt: parts[8] || '',
    ci: parts[9] || '',
    flange: '',
    adapter: '',
    axle: '',
    footBrake: '',
  };
}

/**
 * Get price from price list by category and item code
 */
export function getPriceFromList(
  priceList: PriceListItem[] = [],
  category: string,
  itemCode: string,
  size?: string
): number {
  if (!itemCode || itemCode.toUpperCase() === 'NIL' || itemCode.toUpperCase() === 'NONE') {
    return 0;
  }

  const normCat = (category || '').trim().toUpperCase();
  const normCode = (itemCode || '').trim().toUpperCase();

  const item = (priceList || []).find((p) => {
    const pCat = (p?.category || '').trim().toUpperCase();
    const pCode = (p?.item_code || '').trim().toUpperCase();
    const matchCat = pCat === normCat;
    const matchCode = pCode === normCode || pCode.includes(normCode) || normCode.includes(pCode);
    const matchSize = !size || !p?.size || p.size === size;
    return matchCat && matchCode && matchSize;
  });

  if (item) return Number(item.price);

  // Fallback: if no price list item matched, check if component itself is a numeric price (e.g. "615")
  const isPureNumber = /^\d+(\.\d+)?$/.test(normCode);
  return isPureNumber ? Math.round(parseFloat(normCode)) : 0;
}

/**
 * Calculate base price for an item code across all 13 components
 * Replicates VBA CalculateFinalValue() & GetCValue()/GetWValue()/GetBBValue()
 */
export function calculateBasePrice(
  itemCode: string,
  priceList: PriceListItem[] = [],
  overrides?: Partial<ParsedItemCode>
): number {
  if (!itemCode) return 0;
  const parsed = { ...parseItemCode(itemCode), ...(overrides || {}) };

  let total = 0;

  // 1. Castor base price (C_Value)
  if (parsed.castorType) {
    total += getPriceFromList(priceList, 'Castors', parsed.castorType, parsed.size);
  }

  // 2. Wheel price (W_Value)
  if (parsed.wheelType) {
    total += getPriceFromList(priceList, 'Wheels', parsed.wheelType, parsed.size);
  }

  // 3. Ball bearing price * bearing quantity (BB_Value * tbBbHdn2)
  if (parsed.bearing) {
    const unitPrice = getPriceFromList(priceList, 'BB', parsed.bearing);
    total += unitPrice * (parsed.bearingQty || 1);
  }

  // 4. Circlip price (Cir_Value)
  if (parsed.circlip && parsed.circlip.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'CIR', parsed.circlip);
  }

  // 5. Spacer price (Spcr_Value)
  if (parsed.spacer && parsed.spacer.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'Spacer', parsed.spacer);
  }

  // 6. Bush price (Bush_Value)
  if (parsed.bush && parsed.bush.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'GB', parsed.bush);
  }

  // 7. Thread guard price (Tg_Value)
  if (parsed.threadGuard && parsed.threadGuard.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'TG', parsed.threadGuard);
  }

  // 8. Nut bolt price (Nb_Value)
  if (parsed.nutBolt && parsed.nutBolt.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'NB', parsed.nutBolt);
  }

  // 9. Circlip inner (Ci_Value)
  if (parsed.ci && parsed.ci.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'CI', parsed.ci);
  }

  // 10. Flange price (Flange_Value)
  if (parsed.flange && parsed.flange.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'FC', parsed.flange);
  }

  // 11. Adapter price (Adp_Value)
  if (parsed.adapter && parsed.adapter.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'Adopter', parsed.adapter);
  }

  // 12. Axle price (Axle_Value)
  if (parsed.axle && parsed.axle.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'Axle', parsed.axle);
  }

  // 13. Foot brake price (Fb_Value)
  if (parsed.footBrake && parsed.footBrake.toUpperCase() !== 'NIL') {
    total += getPriceFromList(priceList, 'FB', parsed.footBrake);
  }

  return roundUp(total);
}

/**
 * Calculate individual line item amounts
 * Supports both object LineItemInput and scalar (quantity, rate, discount) arguments
 */
export function calculateLineItem(
  itemOrQty: LineItemInput | any | number,
  maybeRate?: number,
  maybeDiscount?: number
): any {
  if (typeof itemOrQty === 'number') {
    const quantity = itemOrQty || 0;
    const rate = maybeRate || 0;
    const discountPercent = maybeDiscount || 0;
    const amount = quantity * rate;
    const discountAmount = roundUp((amount * discountPercent) / 100);
    return roundUp(amount - discountAmount);
  }

  const quantity = (itemOrQty as any)?.quantity ?? (itemOrQty as any)?.qty ?? 1;
  const rate = itemOrQty?.rate || 0;
  const amount = quantity * rate;
  const discountPercent = (itemOrQty as any)?.discount_percent ?? (itemOrQty as any)?.discount ?? 0;

  // VBA RoundUp on item discount amount
  const discountAmount = roundUp((amount * discountPercent) / 100);
  const finalAmount = roundUp(amount - discountAmount);

  return {
    sno: itemOrQty?.sno,
    item_code: itemOrQty?.item_code,
    description: itemOrQty?.description,
    quantity,
    rate,
    amount,
    discount_percent: discountPercent,
    discount_amount: discountAmount,
    final_amount: finalAmount,
  };
}

/**
 * Calculate quotation financials
 * Matches VBA UpdateCalculations() 1:1
 */
export function calculateFinancials(
  lineItems: (LineItemInput | any)[],
  globalDiscountPercent: number = 0,
  pfPercent: number = 0,
  others: number = 0,
  gstPercent: number = 18
): FinancialCalculationResult {
  // 1. Calculate line items
  const processedItems = (lineItems || []).map((item) => calculateLineItem(item));

  // 2. Subtotal (sum of gross line amounts)
  const grossSubtotal = processedItems.reduce((sum, item) => sum + item.amount, 0);
  const subtotal = roundUp(grossSubtotal);

  // 3. Discount calculation:
  // Check if selective discounts exist on line items
  const hasItemDiscounts = processedItems.some((it) => it.discount_percent > 0);
  let discountAmount = 0;
  let effectiveDiscountPercent = globalDiscountPercent;

  if (hasItemDiscounts) {
    // Sum of individual item discounts (VBA: discValue = discValue + RoundUp(amt(i) * discount(i), 0))
    discountAmount = processedItems.reduce((sum, it) => sum + it.discount_amount, 0);
    effectiveDiscountPercent = subtotal > 0 ? (discountAmount / subtotal) * 100 : 0;
  } else if (globalDiscountPercent > 0) {
    // Uniform discount applied to subtotal (VBA: RoundUp(subtotal * tbDisc.Value / 100, 0))
    discountAmount = roundUp((subtotal * globalDiscountPercent) / 100);
  }

  // 4. Packing & Forwarding (P&F)
  // VBA Rule: packingValue = RoundUp(subtotal * pfPercent / 100, 0)
  // CRITICAL: Applied to subtotal, NOT afterDiscount!
  const pfAmount = roundUp((subtotal * pfPercent) / 100);

  // 5. Taxable Base & GST
  // VBA Rule: (subtotal - discValue + packingValue + others)
  const taxableBase = subtotal - discountAmount + pfAmount + others;
  const gstAmount = roundUp((taxableBase * gstPercent) / 100);

  // 6. Grand Total
  // VBA Rule: totalValue = taxableBase + gstValue
  const total = taxableBase + gstAmount;

  // 7. Automated Remarks (VBA SetRemarksBasedOnDiscount)
  let autoRemarks = '';
  if (hasItemDiscounts) {
    const remarkParts: string[] = [];
    processedItems.forEach((it) => {
      if (it.discount_percent > 0) {
        remarkParts.push(`On Item No. ${it.sno} discount = ${it.discount_percent}%`);
      }
    });
    if (remarkParts.length > 0) {
      autoRemarks = remarkParts.join(', ');
    }
  }

  return {
    subtotal,
    discountPercent: effectiveDiscountPercent,
    discountAmount,
    pfPercent,
    pfAmount,
    pf: pfAmount,
    others,
    taxableBase,
    gstPercent,
    gstAmount,
    gst: gstAmount,
    total,
    amountInWords: convertToIndianCurrency(total),
    remarks: autoRemarks || undefined,
  };
}

/**
 * Convert number to Indian currency words
 * Replicates VBA ConvertToIndianCurrency()
 */
export function convertToIndianCurrency(num: number): string {
  if (isNaN(num) || num === 0) return 'Zero Rupees Only';

  const singleDigits = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convertTwoDigits(n: number): string {
    if (n === 0) return '';
    if (n < 10) return singleDigits[n];
    if (n < 20) return teens[n - 10];
    const t = Math.floor(n / 10);
    const u = n % 10;
    return `${tens[t]}${u ? ' ' + singleDigits[u] : ''}`;
  }

  function convertThreeDigits(n: number): string {
    const h = Math.floor(n / 100);
    const r = n % 100;
    let res = '';
    if (h > 0) res += `${singleDigits[h]} Hundred`;
    if (r > 0) res += `${res ? ' ' : ''}${convertTwoDigits(r)}`;
    return res;
  }

  const intPart = Math.floor(num);

  // Indian Number System: Crores (10,000,000), Lakhs (100,000), Thousands (1,000), Hundreds
  const crore = Math.floor(intPart / 10000000);
  const lakh = Math.floor((intPart % 10000000) / 100000);
  const thousand = Math.floor((intPart % 100000) / 1000);
  const remainder = intPart % 1000;

  const parts: string[] = [];

  if (crore > 0) parts.push(`${crore > 99 ? convertThreeDigits(crore) : convertTwoDigits(crore)} Crore`);
  if (lakh > 0) parts.push(`${convertTwoDigits(lakh)} Lakh`);
  if (thousand > 0) parts.push(`${convertTwoDigits(thousand)} Thousand`);
  if (remainder > 0) parts.push(convertThreeDigits(remainder));

  return `${parts.join(' ')} Rupees Only`;
}

/**
 * Format a number into Indian currency representation (e.g. ₹1,02,896)
 */
export function formatCurrency(amount: number): string {
  if (isNaN(amount) || amount === null || amount === undefined) return '₹0';
  return '₹' + Math.round(amount).toLocaleString('en-IN');
}

/**
 * Wrapper for quotation totals calculation used by data entry workflows & integration tests
 */
export function calculateQuotationTotals(input: {
  items: any[];
  discount_percent?: number;
  pf_percent?: number;
  gst_percent?: number;
  others?: number;
}) {
  const items = input.items || [];
  const hasPrecalculatedFinal = items.some((it) => it.final_amount !== undefined && it.final_amount !== it.amount);

  if (hasPrecalculatedFinal) {
    const subtotal = roundUp(items.reduce((sum, it) => sum + (it.final_amount ?? it.amount ?? 0), 0));
    const grossTotal = items.reduce((sum, it) => sum + (it.amount ?? 0), 0);
    const itemDiscountTotal = items.reduce((sum, it) => sum + (it.discount_amount ?? 0), 0);
    const globalDiscountAmount = roundUp((subtotal * (input.discount_percent || 0)) / 100);
    const totalDiscountAmount = itemDiscountTotal + globalDiscountAmount;
    const pfAmount = roundUp((subtotal * (input.pf_percent || 0)) / 100);
    const others = input.others || 0;
    const taxableBase = subtotal - globalDiscountAmount + pfAmount + others;
    const gstAmount = roundUp((taxableBase * (input.gst_percent || 0)) / 100);
    const total = taxableBase + gstAmount;

    return {
      subtotal,
      grossSubtotal: grossTotal,
      discount_percent: input.discount_percent || 0,
      discount_amount: totalDiscountAmount,
      discountPercent: input.discount_percent || 0,
      discountAmount: totalDiscountAmount,
      pf_percent: input.pf_percent || 0,
      pf_amount: pfAmount,
      pfPercent: input.pf_percent || 0,
      pfAmount,
      others,
      taxableBase,
      gst_percent: input.gst_percent || 0,
      gst_amount: gstAmount,
      gstPercent: input.gst_percent || 0,
      gstAmount,
      total,
      amountInWords: convertToIndianCurrency(total),
    };
  }

  const fin = calculateFinancials(
    items,
    input.discount_percent || 0,
    input.pf_percent || 0,
    input.others || 0,
    input.gst_percent || 0
  );

  return {
    ...fin,
    subtotal: fin.subtotal,
    discount_percent: input.discount_percent || 0,
    discount_amount: fin.discountAmount,
    pf_percent: input.pf_percent || 0,
    pf_amount: fin.pfAmount,
    gst_percent: input.gst_percent || 0,
    gst_amount: fin.gstAmount,
    others: input.others || 0,
    total: fin.total,
  };
}
